import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import { BaseFormComponent } from '../../../../core/components/base-form.component';
import { OrganizationSettingsEditMemberModel } from './form-group/organization-settings-edit-member.model';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { OrganizationSettingsEditMemberFormBuilder } from './form-group/organization-settings-edit-member.form.builder';
import { ControlErrorDirective } from '../../../../shared/directive/control-error.directive';
import { InputText } from 'primeng/inputtext';
import { Button } from 'primeng/button';
import { Select } from 'primeng/select';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ReprezentantOrganizacji } from '../../../../shared/models/reprezentant-organizacji';
import {
  getRolaReprezentantaOrganizacjiDropdownItemsWithoutOwner,
  RolaReprezentantaOrganizacji,
} from '../../../../shared/enums/rola-reprezentanta-organizacji.enum';
import { Card } from 'primeng/card';
import { PreviewDataWrapperComponent } from '../../../../shared/components/preview-data-wrapper/preview-data-wrapper.component';
import { TableModule } from 'primeng/table';
import { Checkbox } from 'primeng/checkbox';
import { AuthService } from '../../../../core/services/auth.service';
import { FormsUtils } from '../../../../shared/utils/forms.utils';
import { ToastSeverity } from '../../../../shared/enums/toast-severity.enum';
import { MessageService } from 'primeng/api';
import { OrganizationRepresentativeUpdateRoleReq } from '../../../../shared/models/custom-types';
import { FileSelectEvent, FileUpload } from 'primeng/fileupload';
import { OrganizacjaApi } from '../../../../shared/services/api/organizacja.api';
import { SpinnerService } from '../../../../core/services/spinner.service';
import { catchError, EMPTY, filter, finalize, Subject, tap } from 'rxjs';
import { Zalacznik } from '../../../../shared/models/zalacznik';
import { ConfirmDialogComponent } from '../../../../shared/components/confirm-dialog/confirm-dialog.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpUtils } from '../../../../shared/utils/http.utils';
import { TooltipModule } from 'primeng/tooltip';
import { DatePipe } from '@angular/common';
import { ErrorMessagesPipe } from '../../../../shared/pipes/error-messages.pipe';
import { ErrorSummary } from '../../../../shared/components/error-summary/error-summary';
import { HttpResponseService } from '../../../../core/services/http-error-response.service';

@Component({
  selector: 'app-organization-settings-edit-member',
  imports: [
    ControlErrorDirective,
    InputText,
    ReactiveFormsModule,
    Button,
    Select,
    Card,
    PreviewDataWrapperComponent,
    TableModule,
    Checkbox,
    FileUpload,
    TooltipModule,
    DatePipe,
    ErrorMessagesPipe,
    ErrorSummary,
  ],
  templateUrl: './organization-settings-edit-member.component.html',
  styleUrl: './organization-settings-edit-member.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [OrganizationSettingsEditMemberFormBuilder, OrganizacjaApi],
})
export class OrganizationSettingsEditMemberComponent extends BaseFormComponent<OrganizationSettingsEditMemberModel> {
  public organizationRepresentative = input.required<ReprezentantOrganizacji>();
  public onDeleteAttachmentSubject = input.required<Subject<number>>();
  public onAddAttachmentSubject = input.required<Subject<Zalacznik>>();
  public organizationRepresentativeAttachment = signal<Zalacznik[]>([]);

  protected readonly authService = inject(AuthService);
  protected readonly dynamicDialogRef = inject(DynamicDialogRef);
  protected readonly organizationMemberRoleOptions =
    getRolaReprezentantaOrganizacjiDropdownItemsWithoutOwner();
  protected readonly RolaReprezentantaOrganizacji =
    RolaReprezentantaOrganizacji;

  readonly #httpResponseService = inject(HttpResponseService);
  readonly #messageService = inject(MessageService);
  readonly #organizationApi = inject(OrganizacjaApi);
  readonly #spinnerService = inject(SpinnerService);
  readonly #dialogService = inject(DialogService);
  readonly #destroyRef = inject(DestroyRef);

  constructor() {
    super();
    this.#initAttachmentSyncEffect();
  }

  protected noInstitutionsSelected = computed(() => {
    return (
      this.formGroup().controls.instytucje.controls.filter(
        (c) => c.value === true,
      ).length === 0
    );
  });

  protected override createFormGroup(): FormGroup<OrganizationSettingsEditMemberModel> {
    const fg = OrganizationSettingsEditMemberFormBuilder.createFormGroup(
      this.organizationRepresentative(),
    );
    this.#setInstytucjeFromOrganizationToFormArray(fg);
    return fg;
  }

  protected downloadAttachment(attachment: Zalacznik) {
    this.#spinnerService.show();
    this.#organizationApi
      .downloadRepresentativePowers$([attachment.id!])
      .pipe(
        tap((resp) => {
          HttpUtils.downloadFile(resp);
        }),
        takeUntilDestroyed(this.#destroyRef),
        finalize(() => {
          this.#spinnerService.hide();
        }),
      )
      .subscribe();
  }

  protected save(): void {
    let selectedInstitutions: string[] = [];

    if (!FormsUtils.isReadyForSave(this.formGroup())) {
      this.#messageService.add({
        severity: ToastSeverity.ERROR,
        summary: 'Wystąpił błąd podczas walidacji formularza',
      });
      return;
    }

    if (
      this.formGroup().controls.role.value === RolaReprezentantaOrganizacji.OUP
    ) {
      const instytucje =
        this.authService.getSelectedOrganization()?.listaInstytucji ?? [];

      const selectedFlags = this.formGroup().controls.instytucje
        .value as boolean[];

      selectedInstitutions = instytucje
        .filter((_, i) => selectedFlags[i])
        .map((inst) => inst.id);
    }

    this.dynamicDialogRef.close({
      idBeneficjetna: this.organizationRepresentative().idBeneficjenta,
      instytucje: selectedInstitutions,
      rola: this.formGroup().controls.role.value,
    } as OrganizationRepresentativeUpdateRoleReq);
  }

  protected onUpload(event: FileSelectEvent): void {
    const newFiles: File[] = Array.from(event.files || []);
    const maxSize = 1000000;

    for (const file of newFiles) {
      if (file.size > maxSize) {
        this.#messageService.add({
          severity: 'error',
          summary: 'Plik jest zbyt duży',
          detail: 'Maksymalny rozmiar pliku to 1 MB.',
        });
        continue;
      }

      this.#spinnerService.show();
      this.#organizationApi
        .saveRepresentativePowerOfAttorney$(
          this.organizationRepresentative().idBeneficjenta!,
          event.files[0],
        )
        .pipe(
          tap((response: Zalacznik) =>
            this.#onUploadRepresentativePowerOfAttorneySuccess(response),
          ),
          catchError((err) => {
            this.#httpResponseService.showErrorMessage(
              err,
              'Wystąpił błąd podczas zapisywania pełnomocnictwa',
            );
            return EMPTY;
          }),
          takeUntilDestroyed(this.#destroyRef),
          finalize(() => this.#spinnerService.hide()),
        )
        .subscribe();
    }
  }

  protected openDeletePowerOfAttorneyDialog(attachment: Zalacznik): void {
    const ref = this.#dialogService.open(ConfirmDialogComponent, {
      closable: true,
      header: `Czy na pewno chcesz usunąć pełnomocnictwo?`,
      closeOnEscape: true,
      modal: true,
      dismissableMask: true,
    });

    if (ref) {
      ref.onClose
        .pipe(
          filter(Boolean),
          tap(() => this.#deletePowerOfAttorney(attachment.id!)),
          takeUntilDestroyed(this.#destroyRef),
        )
        .subscribe();
    }
  }

  #initAttachmentSyncEffect(): void {
    effect(() => {
      const rep = this.organizationRepresentative();
      this.organizationRepresentativeAttachment.set([
        ...(rep.zalaczniki || []),
      ]);
    });
  }

  #setInstytucjeFromOrganizationToFormArray(
    fg: FormGroup<OrganizationSettingsEditMemberModel>,
  ) {
    const formArray = fg.controls.instytucje;
    formArray.clear();

    if (this.authService.getSelectedOrganization() == null) {
      return;
    }

    this.authService
      .getSelectedOrganization()
      ?.listaInstytucji?.forEach((inst) => {
        const isSelected =
          this.organizationRepresentative()?.instytucje?.some(
            (rInst) => rInst === inst.id,
          ) ?? false;
        formArray.push(new FormControl(isSelected));
      });
  }

  #deletePowerOfAttorney(pelnomocictwoId: number): void {
    this.#spinnerService.show();
    this.#organizationApi
      .deleteRepresentativePowerOfAttorney$(pelnomocictwoId)
      .pipe(
        tap(() => this.#deletePowerOfAttorneySuccess(pelnomocictwoId)),
        catchError((err) => {
          this.#httpResponseService.showErrorMessage(
            err,
            'Wystąpił błąd podczas próby usunięcia pełnomocnictwa',
          );
          return EMPTY;
        }),
        finalize(() => this.#spinnerService.hide()),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe();
  }

  #deletePowerOfAttorneySuccess(pelnomocictwoId: number): void {
    this.onDeleteAttachmentSubject().next(pelnomocictwoId);
    this.organizationRepresentativeAttachment.update((value) =>
      value.filter((value1) => value1.id !== pelnomocictwoId),
    );
    this.#messageService.add({
      severity: ToastSeverity.SUCCESS,
      summary: 'Poprawnie usunięto pełnomocnictwo.',
    });
  }

  #onUploadRepresentativePowerOfAttorneySuccess(response: Zalacznik): void {
    this.onAddAttachmentSubject().next(response);
    this.organizationRepresentativeAttachment.update((value) => [
      ...value,
      response,
    ]);
    this.#messageService.add({
      severity: ToastSeverity.SUCCESS,
      summary: 'Poprawnie dodano pełnomocnictwo',
    });
  }
}
